import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Button,
  Divider
} from '@mui/material';
import { 
  AutoAwesome as AutoAwesomeIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Psychology as PsychologyIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const FeaturesPage: React.FC = () => {
  const features = [
    {
      icon: <AutoAwesomeIcon sx={{ fontSize: 40, color: '#2d8cff' }} />,
      title: 'AI-Powered Generation',
      description: 'Create contracts from scratch with simple prompts. Our AI understands legal requirements and generates comprehensive documents tailored to your needs.'
    },
    {
      icon: <SearchIcon sx={{ fontSize: 40, color: '#2d8cff' }} />,
      title: 'Smart Analysis',
      description: 'Our AI analyzes your contracts for potential risks, ambiguities, and missing clauses, providing suggestions to strengthen your legal documents.'
    },
    {
      icon: <EditIcon sx={{ fontSize: 40, color: '#2d8cff' }} />,
      title: 'Intuitive Editing',
      description: 'Edit your documents with our user-friendly interface. Make changes, accept AI suggestions, and customize to your specific requirements.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: '#2d8cff' }} />,
      title: 'Secure Document Management',
      description: 'All your documents are stored securely with enterprise-grade encryption. Control access and share documents safely with stakeholders.'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: '#2d8cff' }} />,
      title: 'Time-Saving Templates',
      description: 'Access a library of legal templates for various business needs. Customize them quickly to save hours of drafting time.'
    },
    {
      icon: <PsychologyIcon sx={{ fontSize: 40, color: '#2d8cff' }} />,
      title: 'Legal Intelligence',
      description: 'Our AI is trained on millions of legal documents and stays updated with the latest legal standards and practices.'
    },
    {
      icon: <CloudUploadIcon sx={{ fontSize: 40, color: '#2d8cff' }} />,
      title: 'Easy Document Upload',
      description: 'Upload existing contracts for analysis, enhancement, and storage. Supports multiple file formats including PDF, Word, and text files.'
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 40, color: '#2d8cff' }} />,
      title: 'Export & Share',
      description: 'Download your documents in multiple formats or share them securely with stakeholders for review and signature.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: '#1a365d', 
        color: 'white',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        pt: 4
      }}>
        {/* Header */}
        <LandingHeader />
        
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography 
                variant="h2" 
                component="h1" 
                fontWeight="800" 
                gutterBottom
                sx={{ 
                  fontSize: { xs: '2rem', md: '3rem' },
                  lineHeight: 1.2
                }}
              >
                Powerful Features for Smart Contract Management
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 4, 
                  opacity: 0.9,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  maxWidth: '600px'
                }}
              >
                Discover how Contract AI streamlines your legal document workflow with cutting-edge AI technology and intuitive tools.
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                component={RouterLink}
                to="/documents/upload?tab=ai"
                startIcon={<AutoAwesomeIcon />}
                sx={{ 
                  bgcolor: '#2d8cff',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor: '#1a7aee'
                  }
                }}
              >
                Try It Now
              </Button>
            </Grid>
          </Grid>
        </Container>
        
        {/* Background decoration */}
        <Box sx={{
          position: 'absolute',
          right: { xs: '-300px', md: '-150px' },
          top: '50%',
          transform: 'translateY(-50%)',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(45,140,255,0.3) 0%, rgba(45,140,255,0) 70%)',
          zIndex: 0
        }} />
      </Box>
      
      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography 
          variant="h3" 
          component="h2" 
          fontWeight="700" 
          textAlign="center"
          sx={{ 
            mb: 2,
            color: '#1a365d',
            fontSize: { xs: '1.75rem', md: '2.5rem' }
          }}
        >
          Everything You Need for Legal Documents
        </Typography>
        <Typography 
          variant="subtitle1" 
          textAlign="center" 
          color="text.secondary" 
          sx={{ 
            mb: 8, 
            maxWidth: '800px', 
            mx: 'auto',
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}
        >
          Our comprehensive suite of features helps you create, analyze, and manage legal documents with ease and confidence.
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    transform: 'translateY(-5px)',
                    borderColor: 'primary.main'
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h5" fontWeight="700" gutterBottom sx={{ color: '#1a365d' }}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* CTA Section */}
      <Box sx={{ bgcolor: '#f8faff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Box 
            sx={{ 
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              borderRadius: '16px',
              border: '1px solid rgba(45,140,255,0.3)',
              bgcolor: 'white',
              boxShadow: '0 10px 40px rgba(0,0,0,0.06)'
            }}
          >
            <Typography 
              variant="h4" 
              fontWeight="700" 
              gutterBottom
              sx={{ 
                color: '#1a365d',
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              Ready to Transform Your Legal Workflow?
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: 4, 
                maxWidth: '600px', 
                mx: 'auto' 
              }}
            >
              Join thousands of professionals who are saving time and reducing errors with Contract AI.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large"
                component={RouterLink}
                to="/register"
                sx={{ 
                  bgcolor: '#2d8cff',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor: '#1a7aee'
                  }
                }}
              >
                Sign Up Free
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                component={RouterLink}
                to="/pricing"
                sx={{ 
                  borderColor: '#1a365d',
                  color: '#1a365d',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  '&:hover': {
                    borderColor: '#1a365d',
                    bgcolor: 'rgba(26,54,93,0.04)'
                  }
                }}
              >
                View Pricing
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default FeaturesPage;
